/**
 * Dixon Gallery and Gardens Map Markers
 * Each marker represents a location, sculpture, or building on the grounds
 *
 * Marker structure:
 * - id: unique identifier for the marker
 * - category: type of marker (sculpture, building, garden, event-space)
 * - coordinates: [x, y] position on the map image
 * - info: display information for the popup
 */

const imagePath = '/dist/map-markers/';

// Marker categories for potential filtering
export const MARKER_CATEGORIES = {
    SCULPTURE: 'sculpture',
    BUILDING: 'building',
    GARDEN: 'garden',
    EVENT_SPACE: 'event-space',
    LANDMARK: 'landmark'
};

const markers = [
    {
        id: 'europa-and-bull',
        category: MARKER_CATEGORIES.SCULPTURE,
        coordinates: [820, 610],
        info: {
            image: imagePath + 'Eur-and-bull.jpg',
            title: 'Europa and the Bull',
            artist: 'Artist Unknown',
            date: 'British, 18th Century',
            medium: 'Marble',
            note: 'Bequest of Mr. and Mrs. Hugo Dixon, 1975.31',
        }
    },
    {
        id: 'carolyn-fera-caerulis',
        category: MARKER_CATEGORIES.SCULPTURE,
        coordinates: [770, 660],
        info: {
            image: imagePath + 'wayne-edge.jpg',
            title: 'Carolyn (Fera caerulis)',
            artist: 'Wayne Edge',
            date: '2016',
            medium: 'Bronze, glass and wire',
            note: 'Museum purchase in memory of Dr. Carolyn Mitchell Kittle with funds provided by Buff Jett Adams, Dabney Coors, Peggy Garrott Peters, and Katie Kittle Powell',
        }
    },
    {
        id: 'orgill-bridge',
        category: MARKER_CATEGORIES.LANDMARK,
        coordinates: [697, 590],
        info: {
            image: imagePath + 'orgill bridge.jpg',
            title: 'Orgill Bridge',
        }
    },
    {
        id: 'family-group',
        category: MARKER_CATEGORIES.SCULPTURE,
        coordinates: [800, 800],
        info: {
            image: imagePath + 'tamboli.jpg',
            title: 'Family Group',
            artist: 'Roy Tamboli',
            date: '1989',
            medium: 'Cast bronze',
            note: 'Gift of the Martin Family, 2019',
        }
    },
    {
        id: 'hughes-pavilion',
        category: MARKER_CATEGORIES.EVENT_SPACE,
        coordinates: [675, 780],
        info: {
            image: imagePath + 'hughes-pav-2022.jpg',
            title: 'Hughes Pavilion and Terrace<br><span class="text-1p6">Event Space</span>',
            description: 'The Hughes Pavilion is available to rent for weddings, private events, corporate groups, and nonprofits.',
            link: 'https://www.dixon.org/venue-rental',
            linkText: 'Learn More',
        }
    },
    {
        id: 'canale-conservatory',
        category: MARKER_CATEGORIES.BUILDING,
        coordinates: [410, 990],
        info: {
            image: imagePath + 'Greenhouse_exterior4.jpg',
            title: 'Canale Conservatory',
        }
    },
    {
        id: 'american-stamina-3',
        category: MARKER_CATEGORIES.SCULPTURE,
        coordinates: [350, 880],
        info: {
            image: imagePath + 'Brian Russell.jpg',
            title: 'American Stamina #3',
            artist: 'Brian Russell',
            date: '2013',
            medium: 'Forged aluminum and cast crystal',
            note: 'Anonymous Gift in honor of Dale Skaggs and the Dixon staff',
        }
    },
    {
        id: 'ceres',
        category: MARKER_CATEGORIES.SCULPTURE,
        coordinates: [400, 760],
        info: {
            image: imagePath + 'Ceres w_ caladiums .jpg',
            title: 'Ceres',
            artist: 'Unknown Artist',
            date: 'n.d.',
            medium: 'Cast iron',
            note: 'Museum purchase with funds provided by Mary and Charles Wurtzburger in memory of Julia "Dolly" Wurtzburger',
        }
    },
    {
        id: 'dixon-residence',
        category: MARKER_CATEGORIES.BUILDING,
        coordinates: [375, 575],
        info: {
            image: imagePath + 'ResidenceExterior.jpg',
            title: 'Dixon Residence',
            description: 'The Dixon Residence was once Margaret and Hugo Dixon\'s home and is the original footprint of the museum.',
            link: 'https://www.dixon.org/our-story',
            linkText: 'Learn More',
        }
    },
    {
        id: 'three-shades',
        category: MARKER_CATEGORIES.SCULPTURE,
        coordinates: [300, 600],
        info: {
            image: imagePath + 'three-shades.jpg',
            title: 'The Three Shades, Modeled',
            artist: 'Auguste Rodin',
            date: '1880-1904, Enlarged 1904',
            medium: 'Cast 4/8, 1985, Foundry Coubertin Bronze',
            note: 'Collection of Iris Cantor',
        }
    },
    {
        id: 'smooth-egg-with-bow',
        category: MARKER_CATEGORIES.SCULPTURE,
        coordinates: [275, 683],
        info: {
            image: imagePath + 'jeff koons egg.jpg',
            title: 'Smooth Egg with Bow <br><span class="text-1p6">(Magenta/Violet)</span>',
            artist: 'Jeff Koons',
            date: 'executed 1994-2009',
            medium: 'Mirror-polished stainless steel with transparent color coating',
            note: 'On loan from Diane B. Wilsey Collection',
        }
    },
    {
        id: 'winegardner-auditorium',
        category: MARKER_CATEGORIES.BUILDING,
        coordinates: [244, 510],
        info: {
            image: imagePath + 'Auditorium.jpg',
            title: 'Winegardner Auditorium',
            link: 'https://www.dixon.org/private-events',
            linkText: 'Learn More',
        }
    },
    {
        id: 'mallory-wurtzburger-galleries',
        category: MARKER_CATEGORIES.BUILDING,
        coordinates: [270, 480],
        info: {
            image: imagePath + 'Mallory_Wurtzburger-galleries-Sucsy.jpg',
            title: 'Mallory/Wurtzburger Galleries',
            link: 'https://www.dixon.org/exhibitions/current',
            linkText: 'View Current Exhibitions',
        }
    },
    {
        id: 'main-galleries',
        category: MARKER_CATEGORIES.BUILDING,
        coordinates: [360, 450],
        info: {
            image: imagePath + 'main galleries.jpg',
            title: 'Main Galleries',
            link: 'https://www.dixon.org/exhibitions/current',
            linkText: 'View Current Exhibitions',
        }
    },
    {
        id: 'park-and-cherry',
        category: MARKER_CATEGORIES.BUILDING,
        coordinates: [295, 435],
        info: {
            image: imagePath + 'park and cherry option 2.jpg',
            title: 'Park + Cherry Café and Museum Store',
            link: 'https://www.dixon.org/parkandcherry',
            linkText: 'Learn More',
        }
    },
    {
        id: 'interactive-gallery',
        category: MARKER_CATEGORIES.BUILDING,
        coordinates: [385, 379],
        info: {
            image: imagePath + 'interactive-gallery-meet-the-dixons.jpg',
            title: 'Interactive Gallery',
            link: 'https://www.dixon.org/exhibitions/current',
            linkText: 'View Current Exhibitions',
        }
    },
    {
        id: 'education-building',
        category: MARKER_CATEGORIES.BUILDING,
        coordinates: [385, 311],
        info: {
            image: imagePath + 'FrontOfFarnsworthEducationBuilding.jpg',
            title: 'Liz and Tommy Education Building',
        }
    },
    {
        id: 'reynolds-greenhouse',
        category: MARKER_CATEGORIES.BUILDING,
        coordinates: [365, 258],
        info: {
            image: imagePath + 'reynolds-greenhouse.jpg',
            title: 'Reynolds Family Greenhouse',
        }
    },
    {
        id: 'butterfly-garden',
        category: MARKER_CATEGORIES.GARDEN,
        coordinates: [320, 238],
        info: {
            image: imagePath + 'butterfly-garden.jpg',
            title: 'Mary Alice Quinn Butterfly Garden',
        }
    },
    {
        id: 'pulperose',
        category: MARKER_CATEGORIES.SCULPTURE,
        coordinates: [449, 273],
        info: {
            image: imagePath + 'pulperose.jpg',
            title: 'Pulperose',
            artist: 'Antoine Poncet Swiss (b. 1928)',
            date: '1968-69',
            medium: 'Rose aurora marble with white marble base',
            note: 'Gift from Sara Lee Corporation, 1988.2',
        }
    },
    {
        id: 'fleuriforme',
        category: MARKER_CATEGORIES.SCULPTURE,
        coordinates: [513, 241],
        info: {
            image: imagePath + 'moss-garden-sculpture-1.jpg',
            title: 'Fleuriforme',
            artist: 'Antoine Poncet Swiss (b. 1928)',
            date: '1968-69',
            medium: 'Rose aurora marble with green marble base',
            note: 'Gift from Sara Lee Corporation, 1988.1',
        }
    },
    {
        id: 'venus-of-memphis',
        category: MARKER_CATEGORIES.SCULPTURE,
        coordinates: [496, 365],
        info: {
            image: imagePath + 'Venus of Memphis (1).jpg',
            title: 'Venus of Memphis',
            artist: 'Wheeler Williams American (1897-1972)',
            date: '1962-64',
            medium: 'Marble',
            note: 'Bequest of Mr. and Mrs. Hugo N. Dixon, 1975.12',
        }
    },
    {
        id: 'caryatid',
        category: MARKER_CATEGORIES.SCULPTURE,
        coordinates: [553, 361],
        info: {
            image: imagePath + 'carroll todd.jpg',
            title: 'Caryatid',
            artist: 'Carroll Todd',
            date: '2019',
            medium: 'Constructed bronze',
            note: 'Museum purchase with funds provided by a bequest from Gayle W. and W. Jack Schuler, 2019.11P',
        }
    },
    {
        id: 'undersea',
        category: MARKER_CATEGORIES.SCULPTURE,
        coordinates: [637, 346],
        info: {
            image: imagePath + 'buckman.jpg',
            title: 'Undersea',
            artist: 'Jim Buchman American, b. 1948',
            date: '2011',
            medium: 'Cast concrete',
            note: 'Gift in honor of Bickie McDonnell by her loving family, 2013.1',
        }
    },
    {
        id: 'untitled-kaneko',
        category: MARKER_CATEGORIES.SCULPTURE,
        coordinates: [700, 214],
        info: {
            image: imagePath + 'tulips-and-kaneko-in-spring.jpg',
            title: 'Untitled',
            artist: 'Jun Kaneko Japanese, U.S. resident (b. 1942)',
            date: '2005 - 2007',
            medium: 'Glazed ceramics',
            note: 'Museum purchase with support from the Mr. and Mrs. Joseph Orgill III Family Fund in the Dixon Gallery and Gardens Endowment Fund, Anonymous Donors, the Theodore W. and Betty J. Eckels Foundation, Liz and Thomas C. Farnsworth, Jr., and Erin Riordan and Kevin Sharp, 2015.3',
        }
    },
];

/**
 * Get markers filtered by category
 * @param {string} category - Category to filter by
 * @returns {Array} Filtered markers
 */
export function getMarkersByCategory(category) {
    return markers.filter(marker => marker.category === category);
}

/**
 * Get a specific marker by ID
 * @param {string} id - Marker ID
 * @returns {Object|undefined} Marker object or undefined
 */
export function getMarkerById(id) {
    return markers.find(marker => marker.id === id);
}

export default markers;
